import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import NavBar from "../components/NavBar";

export default function AdminPanel() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [edited, setEdited] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    if (user.role !== "admin") { nav("/dashboard"); return; }
    load();
    // eslint-disable-next-line
  }, [user]);

  const openOrder = async (orderId) => {
    const r = await api.get(`/orders/${orderId}`);
    setSelected(r.data);
    setEdited(r.data.report?.content || "");
    setMsg("");
  };

  const saveReport = async () => {
    setBusy(true); setMsg("");
    try {
      await api.patch(`/admin/orders/${selected.id}/report`, { content: edited });
      setMsg("Saved");
      await load();
      await openOrder(selected.id);
    } catch (e) { setMsg(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const release = async () => {
    if (!window.confirm("Release this report to the customer?")) return;
    setBusy(true); setMsg("");
    try {
      await api.post(`/admin/orders/${selected.id}/release`);
      setMsg("Released");
      await load();
      await openOrder(selected.id);
    } catch (e) { setMsg(e?.response?.data?.detail || "Release failed"); }
    finally { setBusy(false); }
  };

  const regenerate = async () => {
    setBusy(true); setMsg("");
    try {
      await api.post(`/admin/orders/${selected.id}/regenerate`);
      setMsg("Regeneration triggered. Refresh in ~30s.");
    } catch (e) { setMsg(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!user || user.role !== "admin") return null;

  const stats = orders.reduce((a, o) => { a[o.status] = (a[o.status] || 0) + 1; return a; }, {});

  return (
    <div data-testid="admin-page">
      <NavBar />
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="label-eyebrow">ADMIN / CONTROL ROOM</div>
        <h1 className="font-display text-4xl font-black uppercase tracking-tight mt-2 mb-6">Operations</h1>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 grid-frame mb-8" data-testid="admin-stats">
          {[
            ["Total", orders.length],
            ["Paid", stats.paid || 0],
            ["Intake", stats.intake_received || 0],
            ["In review", stats.report_ready || 0],
            ["Released", stats.released || 0],
          ].map(([k, v]) => (
            <div key={k} className="bg-white p-4">
              <div className="ribbon text-black/60">{k}</div>
              <div className="font-display text-3xl font-black">{v}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-black overflow-x-auto max-h-[70vh] overflow-y-auto">
            <table className="w-full text-left text-sm" data-testid="admin-orders-table">
              <thead className="bg-black text-white sticky top-0">
                <tr>
                  <th className="p-2 ribbon">Customer</th>
                  <th className="p-2 ribbon">Service</th>
                  <th className="p-2 ribbon">£</th>
                  <th className="p-2 ribbon">Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id}
                      className={`border-t border-black/10 cursor-pointer hover:bg-[color:var(--c-bg-alt)] ${selected?.id === o.id ? "bg-[color:var(--c-bg-alt)]" : ""}`}
                      onClick={() => openOrder(o.id)}
                      data-testid={`admin-row-${o.id}`}>
                    <td className="p-2">{o.user_email}</td>
                    <td className="p-2 font-mono text-xs">{o.service_id}</td>
                    <td className="p-2 font-display font-bold">£{o.amount.toFixed(0)}</td>
                    <td className="p-2"><span className="badge badge-default">{o.status}</span></td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr><td colSpan={4} className="p-6 text-center ribbon text-black/50">No orders yet</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="border border-black p-5 max-h-[70vh] overflow-y-auto" data-testid="admin-detail-panel">
            {!selected ? (
              <div className="ribbon text-black/60">Select an order to view & edit the report.</div>
            ) : (
              <>
                <div className="ribbon text-black/60">{selected.id.slice(0,8)} · {selected.user_email}</div>
                <h2 className="font-display text-xl font-bold uppercase">{selected.service_name}</h2>
                <div className="ribbon text-black/60 mt-1 mb-4">
                  Status: <span className="font-bold text-black">{selected.status}</span>
                </div>

                {selected.intake && (
                  <details className="mb-4 border border-black/15 p-3" data-testid="admin-intake-details">
                    <summary className="ribbon cursor-pointer">Intake submission</summary>
                    <pre className="font-mono text-xs whitespace-pre-wrap mt-2">
                      {Object.entries(selected.intake).map(([k, v]) => `${k}: ${v}`).join("\n")}
                    </pre>
                  </details>
                )}

                {selected.report ? (
                  <>
                    <div className="ribbon mb-1">REPORT (markdown editable)</div>
                    <textarea
                      className="textarea w-full"
                      style={{ minHeight: "300px", fontFamily: "JetBrains Mono, monospace", fontSize: 12 }}
                      value={edited}
                      onChange={(e) => setEdited(e.target.value)}
                      data-testid="admin-report-edit"
                    />
                    <div className="flex flex-wrap gap-2 mt-3">
                      <button className="btn btn-sm btn-outline" disabled={busy} onClick={saveReport}
                              data-testid="admin-save-btn">Save edits</button>
                      <button className="btn btn-sm btn-primary" disabled={busy || selected.report.released}
                              onClick={release} data-testid="admin-release-btn">
                        {selected.report.released ? "Released ✓" : "Release to customer →"}
                      </button>
                      <button className="btn btn-sm" disabled={busy} onClick={regenerate}
                              data-testid="admin-regen-btn">Regenerate via AI</button>
                    </div>
                    {msg && <div className="ribbon text-black/70 mt-3" data-testid="admin-msg">{msg}</div>}

                    <div className="ribbon mt-6 mb-2">PREVIEW</div>
                    <div className="prose-report border border-black/15 p-4 bg-white">
                      <ReactMarkdown>{edited || ""}</ReactMarkdown>
                    </div>
                  </>
                ) : (
                  <div className="ribbon text-black/60">No report yet (awaiting intake or generation).</div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
